// GAVION booking (spec §2): name, company, email, phone (all required; the phone is normalised to E.164 by the
// server), the consent line and the purpose of the data (spec §6), and a hidden honeypot field for bots.
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { normalizePhone } from "../../domain/phone";
import { BOOKING_COPY, type PublicLang } from "./copy";

export type FormValues = { name: string; company: string; email: string; phone: string; website: string };
export type FieldError = "required" | "invalidEmail" | "invalidPhone";
export type FieldErrors = Partial<Record<keyof FormValues, FieldError>>;
export const EMPTY_FORM: FormValues = { name: "", company: "", email: "", phone: "", website: "" };
const EMAIL = /^[^@\s<>]+@[^@\s<>]+\.[^@\s<>]+$/;

export function validateBookingForm(v: FormValues): FieldErrors {
  const e: FieldErrors = {};
  if (!v.name.trim()) e.name = "required";
  if (!v.company.trim()) e.company = "required";
  if (!v.email.trim()) e.email = "required";
  else if (!EMAIL.test(v.email.trim())) e.email = "invalidEmail";
  if (!v.phone.trim()) e.phone = "required";
  else if (!normalizePhone(v.phone)) e.phone = "invalidPhone";
  return e;
}

const FIELDS = [
  ["name", "text", "name"],
  ["company", "text", "organization"],
  ["email", "email", "email"],
  ["phone", "tel", "tel"],
] as const;

export const BookingForm = ({
  lang,
  values,
  errors,
  busy,
  onChange,
  onSubmit,
}: {
  lang: PublicLang;
  values: FormValues;
  errors: FieldErrors;
  busy: boolean;
  onChange: (v: FormValues) => void;
  onSubmit: () => void;
}) => {
  const c = BOOKING_COPY[lang];
  return (
    <form
      noValidate
      className="relative mt-4 flex flex-col gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
    >
      {FIELDS.map(([field, type, auto]) => (
        <div key={field} className="flex flex-col gap-1">
          <Label htmlFor={`bk-${field}`}>{c[field]}</Label>
          <Input
            id={`bk-${field}`}
            type={type}
            autoComplete={auto}
            value={values[field]}
            aria-invalid={errors[field] ? true : undefined}
            aria-describedby={errors[field] ? `bk-${field}-error` : undefined}
            onChange={(e) => onChange({ ...values, [field]: e.target.value })}
          />
          {errors[field] && (
            <p id={`bk-${field}-error`} role="alert" className="text-sm text-destructive">
              {c[errors[field]!]}
            </p>
          )}
        </div>
      ))}
      {/* Honeypot (spec §2): invisible and unreachable for a person; a bot that fills it gets « received », nothing more. */}
      <div aria-hidden="true" className="absolute -left-[10000px] top-auto h-px w-px overflow-hidden">
        <label htmlFor="bk-website">{c.website}</label>
        <input
          id="bk-website"
          name="website"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          value={values.website}
          onChange={(e) => onChange({ ...values, website: e.target.value })}
        />
      </div>
      <p className="text-sm text-muted-foreground">{c.consent}</p>
      <p className="text-xs text-muted-foreground">{c.purpose}</p>
      <Button type="submit" disabled={busy}>
        {busy ? c.submitting : c.submit}
      </Button>
    </form>
  );
};
